import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App, ConfigProvider } from 'antd';
import 'antd/dist/reset.css';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRoot from './App';
import PWAInstallPrompt from './components/PWA/PWAInstallPrompt';
import PWARegister from './components/PWA/PWARegister';
import { AuthProvider } from './context/AuthProvider';
import './index.css';
import { store } from './store';
import { handleUnauthorized } from './utils/request';

const rootElement = document.querySelector('#root');
if (!rootElement) {
  throw new Error('Cannot find root element with that id');
}
const root = createRoot(rootElement);
const queryCache = new QueryCache({
  onError: (error: any) => {
    // Handle 401 errors globally for queries
    if (error?.message?.includes('Unauthorized') || error?.status === 401 || error?.message?.includes('Status: 401')) {
      handleUnauthorized();
    }
  },
});

const mutationCache = new MutationCache({
  onError: (error: any) => {
    // Handle 401 errors globally for mutations
    if (error?.message?.includes('Unauthorized') || error?.status === 401 || error?.message?.includes('Status: 401')) {
      handleUnauthorized();
    }
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 401 errors
        if (error?.message?.includes('Unauthorized') || error?.status === 401 || error?.message?.includes('Status: 401')) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

// Ensure globalThis is available for Ant Design
if (typeof globalThis !== 'undefined') {
  (globalThis as any).global = globalThis;
}

root.render(
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#1677ff',
          },
        }}
        prefixCls='ant'
      >
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <PWARegister>
              <React.StrictMode>
                <App>
                  <AppRoot />
                </App>
                <PWAInstallPrompt />
              </React.StrictMode>
            </PWARegister>
          </AuthProvider>
        </Router>
      </ConfigProvider>
    </QueryClientProvider>
  </Provider>
);
